import { GripVertical } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";

/**
 * 行をドラッグで並べ替えるためのハンドル。**先頭列に置く**。
 *
 * 並べ替えの操作は全テーブルでこれに揃える。操作列に上下ボタンを並べる形は
 * 幅を取るうえ、離れた位置へ動かすのに何度も押すことになるため採らない。
 *
 * 掴む場所をハンドルに限っているのは、行全体を掴めるようにするとセルの中の
 * 文字を選べなくなり、インライン編集を始めるつもりの操作がドラッグになって
 * しまうため。落とす側の処理は [[DataTable]] の `onReorderRow` が受け持つ。
 *
 * ドラッグできない環境のために、フォーカスして ↑↓ キーでも 1 行ずつ動かせる。
 */
export default function RowDragHandle({
  rowId,
  label,
  isDisabled = false,
  canMoveUp,
  canMoveDown,
  onMove,
  children,
}: {
  /** `getRowId` が返す行 id。落とす側が行を特定するのに使う。 */
  rowId: string | number;
  /** 読み上げ用の行の呼び名（例: `3 行目`）。 */
  label: string;
  isDisabled?: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** ↑↓ キーで 1 行ぶん動かす。 */
  onMove: (direction: -1 | 1) => void;
  /** ハンドルの右に並べる内容（行番号など）。 */
  children?: ReactNode;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (isDisabled) return;
    if (event.key === "ArrowUp" && canMoveUp) {
      event.preventDefault();
      onMove(-1);
    }
    if (event.key === "ArrowDown" && canMoveDown) {
      event.preventDefault();
      onMove(1);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <span
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-label={`${label}を並べ替え`}
        title="ドラッグで並べ替え（↑↓ キーでも動かせます）"
        draggable={!isDisabled}
        onDragStart={(event) => {
          event.dataTransfer.setData("text/plain", String(rowId));
          event.dataTransfer.effectAllowed = "move";
          // 既定の見た目はハンドルのアイコンだけで、何を掴んだのか分からない。
          // 行そのものをカーソルに付いてくる絵にする。
          const row = event.currentTarget.closest("tr");
          if (row) event.dataTransfer.setDragImage(row, 24, 20);
        }}
        onKeyDown={onKeyDown}
        className={[
          "shrink-0 rounded p-0.5 text-default-400",
          isDisabled
            ? "opacity-40"
            : "cursor-grab hover:bg-default-100 hover:text-brand-500 active:cursor-grabbing",
        ].join(" ")}
      >
        <GripVertical size={14} />
      </span>
      {children}
    </div>
  );
}
